// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN KYC MANAGEMENT PAGE                     ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Shield,
  Search,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Download,
  FileText,
  User,
  Calendar,
  MapPin,
  CreditCard,
  Camera,
  Video,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  RotateCcw,
  Image,
  ExternalLink,
  Loader2,
  Trash2
} from 'lucide-react'
import { KycAdminAPI } from '../../services/adminApi'

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pendiente' },
    in_review: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Eye, label: 'En Revisión' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle, label: 'Aprobado' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Rechazado' },
    declined: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Declinado' },
    not_started: { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: Clock, label: 'No Iniciado' },
    in_progress: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: RefreshCw, label: 'En Progreso' },
    expired: { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: AlertTriangle, label: 'Expirado' },
    abandoned: { bg: 'bg-slate-500/20', text: 'text-slate-400', icon: XCircle, label: 'Abandonado' },
  }

  const { bg, text, icon: Icon, label } = config[status] || config.pending

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  )
}

// Stats Card
const StatCard = ({ title, value, icon: Icon, color, trend }) => (
  <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-slate-400 text-sm">{title}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
        {trend && (
          <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}% vs mes anterior
          </p>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
)

export default function KycPage() {
  const [activeTab, setActiveTab] = useState('pending')
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [pendingReviews, setPendingReviews] = useState([])
  const [sessions, setSessions] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [userDetails, setUserDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  // New state for media/images
  const [sessionMedia, setSessionMedia] = useState(null)
  const [loadingMedia, setLoadingMedia] = useState(false)
  const [fullVerificationData, setFullVerificationData] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteFromDidit, setDeleteFromDidit] = useState(true)

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      const response = await KycAdminAPI.getDashboard()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching KYC stats:', error)
    }
  }

  // Fetch pending reviews
  const fetchPendingReviews = async () => {
    try {
      const response = await KycAdminAPI.getPendingReviews()
      if (response.success) {
        setPendingReviews(response.data || [])
      }
    } catch (error) {
      console.error('Error fetching pending reviews:', error)
    }
  }

  // Fetch all sessions
  const fetchSessions = async () => {
    try {
      const params = {}
      if (statusFilter !== 'all') params.status = statusFilter
      const response = await KycAdminAPI.getSessions(params)
      if (response.success) {
        setSessions(response.data || [])
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    }
  }

  // Load user KYC details
  const loadUserDetails = async (userId) => {
    setLoadingDetails(true)
    setSessionMedia(null)
    setFullVerificationData(null)
    try {
      const response = await KycAdminAPI.getUserKyc(userId)
      if (response.success) {
        setUserDetails(response.data)

        // If there's a session, load media and full verification data
        if (response.data.sessions?.[0]?.id) {
          loadSessionMedia(response.data.sessions[0].id)
        }
      }
    } catch (error) {
      console.error('Error loading user details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }

  // Load session media (images, videos)
  const loadSessionMedia = async (sessionId) => {
    setLoadingMedia(true)
    try {
      // Try to get full verification data first (includes media)
      const fullDataResponse = await KycAdminAPI.getFullVerificationData(sessionId)
      if (fullDataResponse.success) {
        setFullVerificationData(fullDataResponse.data)
        setSessionMedia(fullDataResponse.data.media || {})
      } else {
        // Fallback to just media endpoint
        const mediaResponse = await KycAdminAPI.getSessionMedia(sessionId)
        if (mediaResponse.success) {
          setSessionMedia(mediaResponse.data.media || {})
        }
      }
    } catch (error) {
      console.error('Error loading session media:', error)
      // Try direct media endpoint as fallback
      try {
        const mediaResponse = await KycAdminAPI.getSessionMedia(sessionId)
        if (mediaResponse.success) {
          setSessionMedia(mediaResponse.data.media || {})
        }
      } catch (e) {
        console.error('Fallback media load failed:', e)
      }
    } finally {
      setLoadingMedia(false)
    }
  }

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchStats(), fetchPendingReviews(), fetchSessions()])
      setLoading(false)
    }
    loadData()
  }, [])

  // Reload sessions when filter changes
  useEffect(() => {
    fetchSessions()
  }, [statusFilter])

  // Handle approve
  const handleApprove = async (userId, sessionId) => {
    setActionLoading(true)
    try {
      const response = await KycAdminAPI.approveKyc(userId, { sessionId })
      if (response.success) {
        await Promise.all([fetchStats(), fetchPendingReviews(), fetchSessions()])
        setSelectedUser(null)
        setUserDetails(null)
      }
    } catch (error) {
      console.error('Error approving KYC:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Handle reject
  const handleReject = async (userId, sessionId) => {
    if (!rejectReason.trim()) return

    setActionLoading(true)
    try {
      const response = await KycAdminAPI.rejectKyc(userId, rejectReason)
      if (response.success) {
        await Promise.all([fetchStats(), fetchPendingReviews(), fetchSessions()])
        setSelectedUser(null)
        setUserDetails(null)
        setShowRejectModal(false)
        setRejectReason('')
      }
    } catch (error) {
      console.error('Error rejecting KYC:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Handle reset
  const handleReset = async (userId) => {
    setActionLoading(true)
    try {
      const response = await KycAdminAPI.resetKyc(userId)
      if (response.success) {
        await Promise.all([fetchStats(), fetchPendingReviews(), fetchSessions()])
        setSelectedUser(null)
        setUserDetails(null)
      }
    } catch (error) {
      console.error('Error resetting KYC:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Download PDF
  const handleDownloadPdf = async (sessionId) => {
    try {
      const response = await KycAdminAPI.downloadPdf(sessionId)
      if (response.success) {
        const a = document.createElement('a')
        a.href = response.data.url
        a.download = response.data.filename
        a.click()
        URL.revokeObjectURL(response.data.url)
      }
    } catch (error) {
      console.error('Error downloading PDF:', error)
    }
  }

  // Handle delete KYC
  const handleDelete = async () => {
    if (!deleteTarget) return

    setActionLoading(true)
    try {
      let response
      if (deleteTarget.type === 'session') {
        // Delete single session
        response = await KycAdminAPI.deleteSession(deleteTarget.sessionId, deleteFromDidit)
      } else {
        // Delete all user KYC
        response = await KycAdminAPI.deleteUserKyc(deleteTarget.userId, deleteFromDidit)
      }

      if (response.success) {
        await Promise.all([fetchStats(), fetchPendingReviews(), fetchSessions()])
        setSelectedUser(null)
        setUserDetails(null)
        setShowDeleteModal(false)
        setDeleteTarget(null)
        setDeleteFromDidit(true)
      }
    } catch (error) {
      console.error('Error deleting KYC:', error)
      alert('Error al eliminar KYC: ' + (error.message || 'Error desconocido'))
    } finally {
      setActionLoading(false)
    }
  }

  // Filter sessions
  const filteredSessions = sessions.filter(session => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      session.user_email?.toLowerCase().includes(search) ||
      session.user_first_name?.toLowerCase().includes(search) ||
      session.user_last_name?.toLowerCase().includes(search) ||
      session.full_name?.toLowerCase().includes(search)
    )
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Verificación KYC</h1>
          <p className="text-slate-400 mt-1">Gestiona las verificaciones de identidad</p>
        </div>
        <button
          onClick={() => Promise.all([fetchStats(), fetchPendingReviews(), fetchSessions()])}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Pendientes de Revisión"
          value={stats?.pendingReviews || 0}
          icon={Clock}
          color="bg-yellow-500"
        />
        <StatCard
          title="Aprobados"
          value={stats?.statusCounts?.approved || 0}
          icon={CheckCircle}
          color="bg-green-500"
        />
        <StatCard
          title="Rechazados"
          value={stats?.statusCounts?.rejected || 0}
          icon={XCircle}
          color="bg-red-500"
        />
        <StatCard
          title="Tasa de Aprobación"
          value={`${stats?.approvalRate || 0}%`}
          icon={Shield}
          color="bg-blue-500"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-4">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-amber-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          Pendientes ({pendingReviews.length})
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'sessions'
              ? 'bg-amber-500 text-white'
              : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          Todas las Sesiones
        </button>
      </div>

      {/* Content */}
      {activeTab === 'pending' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          {pendingReviews.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500/50 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No hay revisiones pendientes</h3>
              <p className="text-slate-400">Todas las verificaciones han sido procesadas</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {pendingReviews.map((review) => (
                <div key={review.id} className="p-4 hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {review.first_name} {review.last_name}
                        </p>
                        <p className="text-sm text-slate-400">{review.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={review.session_status || review.kyc_status} />
                      <button
                        onClick={() => {
                          setSelectedUser(review)
                          loadUserDetails(review.id)
                        }}
                        className="px-3 py-1.5 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg transition-colors flex items-center gap-1.5"
                      >
                        <Eye className="w-4 h-4" />
                        Revisar
                      </button>
                    </div>
                  </div>
                  {review.document_type && (
                    <div className="mt-3 flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <CreditCard className="w-4 h-4" />
                        {review.document_type}
                      </span>
                      {review.document_country && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {review.document_country}
                        </span>
                      )}
                      {review.face_match_score && (
                        <span className="flex items-center gap-1">
                          <Camera className="w-4 h-4" />
                          Face Match: {review.face_match_score}%
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            >
              <option value="all">Todos los estados</option>
              <option value="approved">Aprobados</option>
              <option value="declined">Rechazados</option>
              <option value="in_review">En Revisión</option>
              <option value="in_progress">En Progreso</option>
              <option value="not_started">No Iniciados</option>
            </select>
          </div>

          {/* Sessions Table */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Usuario</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Estado</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Documento</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Fecha</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredSessions.map((session) => (
                    <tr key={session.id} className="hover:bg-slate-700/50 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-white">
                            {session.user_first_name} {session.user_last_name}
                          </p>
                          <p className="text-sm text-slate-400">{session.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={session.status} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-300">{session.document_type || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-400 text-sm">
                          {new Date(session.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleDownloadPdf(session.id)}
                            className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                            title="Descargar PDF"
                          >
                            <FileText className="w-4 h-4 text-slate-400" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser({ id: session.user_id })
                              loadUserDetails(session.user_id)
                            }}
                            className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4 text-slate-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setSelectedUser(null)
              setUserDetails(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <div>
                  <h2 className="text-xl font-bold text-white">Detalles de Verificación</h2>
                  {userDetails?.user && (
                    <p className="text-slate-400 mt-1">
                      {userDetails.user.first_name} {userDetails.user.last_name} - {userDetails.user.email}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedUser(null)
                    setUserDetails(null)
                  }}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                  </div>
                ) : userDetails ? (
                  <div className="space-y-6">
                    {/* User Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-slate-900/50 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-slate-400 mb-3">Estado Actual</h3>
                        <StatusBadge status={userDetails.user.kyc_status} />
                      </div>
                      <div className="bg-slate-900/50 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-slate-400 mb-3">Verificado</h3>
                        <p className="text-white">
                          {userDetails.user.kyc_verified_at
                            ? new Date(userDetails.user.kyc_verified_at).toLocaleString()
                            : 'No verificado'}
                        </p>
                      </div>
                    </div>

                    {/* Latest Session */}
                    {userDetails.sessions?.[0] && (
                      <div className="bg-slate-900/50 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-slate-400 mb-4 flex items-center gap-2">
                          Latest Verification Session
                          {loadingMedia && <Loader2 className="w-3 h-3 animate-spin" />}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-slate-500">Document Type</p>
                            <p className="text-white font-medium">
                              {fullVerificationData?.verification?.idVerification?.documentType ||
                               fullVerificationData?.diditRawData?.id_verification?.document_type ||
                               userDetails.sessions[0].document_type || '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Country</p>
                            <p className="text-white font-medium">
                              {fullVerificationData?.verification?.idVerification?.documentCountry ||
                               fullVerificationData?.diditRawData?.id_verification?.country ||
                               fullVerificationData?.diditRawData?.country ||
                               userDetails.sessions[0].document_country || '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Full Name</p>
                            <p className="text-white font-medium">
                              {fullVerificationData?.verification?.idVerification?.fullName ||
                               fullVerificationData?.diditRawData?.full_name ||
                               fullVerificationData?.diditRawData?.id_verification?.full_name ||
                               userDetails.sessions[0].full_name || '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Face Match Score</p>
                            <p className="text-white font-medium">
                              {(fullVerificationData?.verification?.faceMatch?.score ||
                                fullVerificationData?.diditRawData?.face_match?.score ||
                                userDetails.sessions[0].face_match_score)
                                ? `${fullVerificationData?.verification?.faceMatch?.score ||
                                    fullVerificationData?.diditRawData?.face_match?.score ||
                                    userDetails.sessions[0].face_match_score}%`
                                : '-'}
                            </p>
                          </div>
                        </div>

                        {/* Verification Results */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className={`px-3 py-2 rounded-lg ${
                            (fullVerificationData?.verification?.idVerification?.status ||
                             fullVerificationData?.diditRawData?.id_verification?.status ||
                             userDetails.sessions[0].id_verification_status) === 'Approved'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}>
                            <p className="text-xs opacity-70">ID Verification</p>
                            <p className="font-medium">
                              {fullVerificationData?.verification?.idVerification?.status ||
                               fullVerificationData?.diditRawData?.id_verification?.status ||
                               userDetails.sessions[0].id_verification_status || 'Pending'}
                            </p>
                          </div>
                          <div className={`px-3 py-2 rounded-lg ${
                            (fullVerificationData?.verification?.liveness?.status ||
                             fullVerificationData?.diditRawData?.liveness?.status ||
                             userDetails.sessions[0].liveness_status) === 'Approved'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}>
                            <p className="text-xs opacity-70">Liveness</p>
                            <p className="font-medium">
                              {fullVerificationData?.verification?.liveness?.status ||
                               fullVerificationData?.diditRawData?.liveness?.status ||
                               userDetails.sessions[0].liveness_status || 'Pending'}
                            </p>
                          </div>
                          <div className={`px-3 py-2 rounded-lg ${
                            (fullVerificationData?.verification?.faceMatch?.status ||
                             fullVerificationData?.diditRawData?.face_match?.status ||
                             userDetails.sessions[0].face_match_status) === 'Approved'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}>
                            <p className="text-xs opacity-70">Face Match</p>
                            <p className="font-medium">
                              {fullVerificationData?.verification?.faceMatch?.status ||
                               fullVerificationData?.diditRawData?.face_match?.status ||
                               userDetails.sessions[0].face_match_status || 'Pending'}
                            </p>
                          </div>
                          <div className={`px-3 py-2 rounded-lg ${
                            (fullVerificationData?.verification?.aml?.status ||
                             fullVerificationData?.diditRawData?.aml?.status ||
                             userDetails.sessions[0].aml_status) === 'Approved'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}>
                            <p className="text-xs opacity-70">AML Check</p>
                            <p className="font-medium">
                              {fullVerificationData?.verification?.aml?.status ||
                               fullVerificationData?.diditRawData?.aml?.status ||
                               userDetails.sessions[0].aml_status || 'Pending'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Document Images & Media */}
                    <div className="bg-slate-900/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Documentos e Imágenes
                        </h3>
                        {loadingMedia && (
                          <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                        )}
                      </div>

                      {loadingMedia ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="text-center">
                            <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-2" />
                            <p className="text-sm text-slate-400">Cargando imágenes...</p>
                          </div>
                        </div>
                      ) : sessionMedia && Object.values(sessionMedia).some(v => v) ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {/* Portrait / ID Photo */}
                          {sessionMedia.portrait && (
                            <div
                              className="relative group cursor-pointer"
                              onClick={() => setSelectedImage({ url: sessionMedia.portrait, title: 'Foto del Documento' })}
                            >
                              <div className="aspect-[3/4] bg-slate-800 rounded-lg overflow-hidden border border-slate-700 group-hover:border-amber-500 transition-colors">
                                <img
                                  src={sessionMedia.portrait}
                                  alt="Portrait"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-slate-500 text-xs">No disponible</span></div>'
                                  }}
                                />
                              </div>
                              <p className="text-xs text-slate-400 mt-1 text-center">Foto ID</p>
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          )}

                          {/* Document Front */}
                          {sessionMedia.documentFront && (
                            <div
                              className="relative group cursor-pointer"
                              onClick={() => setSelectedImage({ url: sessionMedia.documentFront, title: 'Documento (Frente)' })}
                            >
                              <div className="aspect-[3/4] bg-slate-800 rounded-lg overflow-hidden border border-slate-700 group-hover:border-amber-500 transition-colors">
                                <img
                                  src={sessionMedia.documentFront}
                                  alt="Document Front"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-slate-500 text-xs">No disponible</span></div>'
                                  }}
                                />
                              </div>
                              <p className="text-xs text-slate-400 mt-1 text-center">Doc. Frente</p>
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          )}

                          {/* Document Back */}
                          {sessionMedia.documentBack && (
                            <div
                              className="relative group cursor-pointer"
                              onClick={() => setSelectedImage({ url: sessionMedia.documentBack, title: 'Documento (Reverso)' })}
                            >
                              <div className="aspect-[3/4] bg-slate-800 rounded-lg overflow-hidden border border-slate-700 group-hover:border-amber-500 transition-colors">
                                <img
                                  src={sessionMedia.documentBack}
                                  alt="Document Back"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-slate-500 text-xs">No disponible</span></div>'
                                  }}
                                />
                              </div>
                              <p className="text-xs text-slate-400 mt-1 text-center">Doc. Reverso</p>
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          )}

                          {/* Selfie */}
                          {sessionMedia.selfie && (
                            <div
                              className="relative group cursor-pointer"
                              onClick={() => setSelectedImage({ url: sessionMedia.selfie, title: 'Selfie (Liveness)' })}
                            >
                              <div className="aspect-[3/4] bg-slate-800 rounded-lg overflow-hidden border border-slate-700 group-hover:border-amber-500 transition-colors">
                                <img
                                  src={sessionMedia.selfie}
                                  alt="Selfie"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-slate-500 text-xs">No disponible</span></div>'
                                  }}
                                />
                              </div>
                              <p className="text-xs text-slate-400 mt-1 text-center">Selfie</p>
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          )}

                          {/* Liveness Video */}
                          {sessionMedia.livenessVideo && (
                            <div className="col-span-2">
                              <div className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700">
                                <video
                                  src={sessionMedia.livenessVideo}
                                  controls
                                  className="w-full"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-24"><span class="text-slate-500 text-sm">Video no disponible</span></div>'
                                  }}
                                >
                                  Tu navegador no soporta videos.
                                </video>
                              </div>
                              <p className="text-xs text-slate-400 mt-1 text-center flex items-center justify-center gap-1">
                                <Video className="w-3 h-3" />
                                Video de Liveness
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No hay imágenes disponibles para esta verificación</p>
                          <p className="text-xs mt-1">Las imágenes estarán disponibles una vez completada la verificación</p>
                        </div>
                      )}
                    </div>

                    {/* Additional Verification Details */}
                    {fullVerificationData?.verification && (
                      <div className="bg-slate-900/50 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-slate-400 mb-4 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Datos Extraídos del Documento
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          {fullVerificationData.verification.idVerification?.documentNumber && (
                            <div>
                              <p className="text-xs text-slate-500">Número de Documento</p>
                              <p className="text-white font-mono">{fullVerificationData.verification.idVerification.documentNumber}</p>
                            </div>
                          )}
                          {fullVerificationData.verification.idVerification?.dateOfBirth && (
                            <div>
                              <p className="text-xs text-slate-500">Fecha de Nacimiento</p>
                              <p className="text-white">{fullVerificationData.verification.idVerification.dateOfBirth}</p>
                            </div>
                          )}
                          {fullVerificationData.verification.idVerification?.nationality && (
                            <div>
                              <p className="text-xs text-slate-500">Nacionalidad</p>
                              <p className="text-white">{fullVerificationData.verification.idVerification.nationality}</p>
                            </div>
                          )}
                          {fullVerificationData.verification.idVerification?.gender && (
                            <div>
                              <p className="text-xs text-slate-500">Género</p>
                              <p className="text-white">{fullVerificationData.verification.idVerification.gender}</p>
                            </div>
                          )}
                          {fullVerificationData.verification.idVerification?.documentExpiry && (
                            <div>
                              <p className="text-xs text-slate-500">Fecha de Expiración</p>
                              <p className="text-white">{fullVerificationData.verification.idVerification.documentExpiry}</p>
                            </div>
                          )}
                          {fullVerificationData.verification.faceMatch?.score && (
                            <div>
                              <p className="text-xs text-slate-500">Puntuación Face Match</p>
                              <p className="text-white font-bold text-lg">{fullVerificationData.verification.faceMatch.score}%</p>
                            </div>
                          )}
                        </div>

                        {/* Warnings */}
                        {fullVerificationData.verification.warnings?.length > 0 && (
                          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                            <p className="text-xs text-yellow-400 font-medium mb-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Advertencias
                            </p>
                            <ul className="text-sm text-yellow-300 list-disc list-inside">
                              {fullVerificationData.verification.warnings.map((warning, idx) => (
                                <li key={idx}>{typeof warning === 'string' ? warning : JSON.stringify(warning)}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Rejection Reasons from Didit */}
                        {fullVerificationData.verification.rejectionReasons?.length > 0 && (
                          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-xs text-red-400 font-medium mb-1">Razones de Rechazo (Didit)</p>
                            <ul className="text-sm text-red-300 list-disc list-inside">
                              {fullVerificationData.verification.rejectionReasons.map((reason, idx) => (
                                <li key={idx}>{typeof reason === 'string' ? reason : reason.reason || JSON.stringify(reason)}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rejection Reason */}
                    {userDetails.user.kyc_rejection_reason && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-red-400 mb-2">Razón de Rechazo</h3>
                        <p className="text-white">{userDetails.user.kyc_rejection_reason}</p>
                      </div>
                    )}

                    {/* Session History */}
                    {userDetails.sessions?.length > 1 && (
                      <div className="bg-slate-900/50 rounded-xl p-4">
                        <h3 className="text-sm font-medium text-slate-400 mb-3">Historial de Sesiones</h3>
                        <div className="space-y-2">
                          {userDetails.sessions.slice(1).map((session) => (
                            <div key={session.id} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                              <div className="flex items-center gap-3">
                                <StatusBadge status={session.status} />
                                <span className="text-slate-400 text-sm">
                                  {new Date(session.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <span className="text-slate-500 text-sm">{session.document_type || '-'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-8">No se pudieron cargar los detalles</p>
                )}
              </div>

              {/* Actions */}
              {userDetails && (
                <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-700">
                  {/* Delete button on left */}
                  <button
                    onClick={() => {
                      setDeleteTarget({
                        type: 'user',
                        userId: userDetails.user.id,
                        userName: `${userDetails.user.first_name} ${userDetails.user.last_name}`,
                        sessionId: userDetails.sessions?.[0]?.id
                      })
                      setShowDeleteModal(true)
                    }}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar KYC
                  </button>

                  {/* Action buttons on right */}
                  <div className="flex items-center gap-3">
                    {userDetails.user.kyc_status !== 'approved' && (
                      <>
                        <button
                          onClick={() => handleReset(userDetails.user.id)}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Reiniciar
                        </button>
                        <button
                          onClick={() => setShowRejectModal(true)}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Rechazar
                        </button>
                        <button
                          onClick={() => handleApprove(userDetails.user.id, userDetails.sessions?.[0]?.id)}
                          disabled={actionLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {actionLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                          Aprobar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setShowRejectModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-white mb-4">Rechazar Verificación</h3>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ingresa la razón del rechazo..."
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 resize-none"
                rows={4}
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleReject(userDetails?.user?.id, userDetails?.sessions?.[0]?.id)}
                  disabled={!rejectReason.trim() || actionLoading}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Confirmar Rechazo'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 p-2 bg-slate-800 hover:bg-slate-700 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              {/* Title */}
              <h3 className="absolute -top-12 left-0 text-white font-medium">
                {selectedImage.title}
              </h3>

              {/* Image */}
              <div className="bg-slate-900 rounded-xl overflow-hidden border border-slate-700">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              </div>

              {/* Download button */}
              <div className="flex justify-center mt-4">
                <a
                  href={selectedImage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir en nueva pestaña
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => {
              setShowDeleteModal(false)
              setDeleteTarget(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Eliminar KYC</h3>
                  <p className="text-sm text-slate-400">
                    {deleteTarget.userName || 'Este usuario'}
                  </p>
                </div>
              </div>

              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-300">
                  <strong>Advertencia:</strong> Esta acción eliminará todos los datos de verificación KYC
                  del usuario. El usuario deberá completar el proceso de verificación nuevamente.
                </p>
              </div>

              <label className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={deleteFromDidit}
                  onChange={(e) => setDeleteFromDidit(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 text-amber-500 focus:ring-amber-500 focus:ring-offset-slate-800"
                />
                <div>
                  <p className="text-sm text-white">También eliminar de Didit</p>
                  <p className="text-xs text-slate-400">
                    Elimina la sesión del proveedor de verificación externo
                  </p>
                </div>
              </label>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setDeleteTarget(null)
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Eliminar KYC
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
